import React, { Fragment, useEffect, useMemo, useState } from "react";
import { keyBy } from "lodash";
import { useSearchParams } from "wouter";

import { KeyPerformanceIndicators, PerformanceKeyEnum, Language } from "../../../../components/src";
import { DonutChart } from "../../../../components/src/charts/DonutChart";

import { PerformancePeriodSelector } from "./PerformancePeriodSelector";
import { useQuery } from "urql";
import { InstrumentModelTypeEnum, Party, PartyAccountType, TradingManagerColumn } from "../../types.generated";
import { getPieData } from "../../../../graphql/src/performance/queries";
import { useQueryState } from "../../../../components/src/use-query-state";
import { useProcessedData } from "./hooks";
import { SelectedRowType, ProcessedData } from "./interfaces";
import { usePrevious } from "../../../../components/src/common/Utils";
import { PerformanceTimeSeriesChart } from "../../components/PerformanceTimeSeriesChart";
import { usePerformance } from "./performanceGraphHooks";
import { excludeInAttribution, getTopBottomInstruments, getTopBottomInstrumentsFromSelectedRow, isValidRow } from "./functions";
import { AttributionGraph, AttributionSelectorEnum, PlotTypeEnum } from "./Attribution";
import { PrintButton } from "../../components/PrintButton";

interface Props {
    client: Partial<Party>;
    data: any;
    endDate: string;
}

export function PerformanceTabSimple({ client, data, endDate }: Props): React.ReactElement {
    const [{ fetching: loading, error, data: paiData }] = useQuery({
        query: getPieData,
        variables: { clientId: client._id, endDate: endDate },
        pause: client ? false : true,
        requestPolicy: "cache-and-network"
    });
    const [printView, setPrintView] = useQueryState("print", "true");
    const [sumLevelPieChart, setSumLevelPieChart] = useQueryState("sumLevelPieChart", "false");
    const [startDate] = useQueryState("startDate", client.firstTradeDate);
    const [, setQueryArgs] = useSearchParams();

    const processedData: ProcessedData = useProcessedData({ endDate, startDate, data });

    const [selectedRow, setSelectedRow] = useState<SelectedRowType>({
        name: "",
        performance: null,
        values: [],
        instrumentPerformances: [],
        benchmarks: [],
        riskFree: null,
        startDate: startDate,
        endDate: endDate,
        dateSeries: [],
        positionFlag: []
    });
    const previousSelectedRow: SelectedRowType = usePrevious(selectedRow);
    const previousData: any = usePrevious(data);

    const { performanceSeries: _, alignedTimeSeries, value } = usePerformance({ ...selectedRow, endDate, startDate });
    const plotData = useMemo(() => {
        if (!paiData || !client) return null;

        const accountsById = keyBy(client.accounts, "_id");
        const labels: string[] = [];
        const values: number[] = [];
        const positionByAccount: Record<string, TradingManagerColumn> = {};
        for (const column of paiData.tradingmanager) {
            let label = column.account.description;
            let accountId = column.accountId;
            if (sumLevelPieChart === "true") {
                const account = accountsById[accountId];
                const parentAccount = accountsById[account.parentAccountId];
                label = parentAccount.description;
                accountId = account.parentAccountId;
            }
            labels.push(label);
            values.push(column.exposure);
            positionByAccount[accountId] = column as any;
        }

        return {
            slices: labels.map((label, index) => ({ label, value: Number(values[index]) })),
            positionByAccount
        };
    }, [client, paiData, sumLevelPieChart]);

    useEffect(() => {
        if (processedData) {
            const { firstSelectedRow, dateSeries } = processedData;

            const startDateAttribution =
                startDate > dateSeries[0] || startDate < dateSeries[dateSeries.length - 1] ? startDate : dateSeries[0];
            // `selectedRow` holds deep snapshots of series/values taken from the payload it was built
            // from, so it must be rebuilt whenever a new performance payload arrives — both on client
            // switches and when a refetch delivers updated numbers for the same client.
            if (selectedRow.name === "" || data !== previousData) {
                setSelectedRow(firstSelectedRow);
                setQueryArgs({ startDate: startDateAttribution, endDate });
            } else if (selectedRow.name !== previousSelectedRow.name) {
                setQueryArgs({ startDate: startDateAttribution, endDate });
            }
        }
    }, [client, data, previousData, endDate, previousSelectedRow, processedData, selectedRow, setQueryArgs, startDate]);

    const handlePrintView = (e) => {
        setPrintView(e.target.checked);
    };

    const handlePieChartLevel = (e) => {
        setSumLevelPieChart(e.target.checked);
    };

    if (!processedData) return <div></div>;

    if (!client.firstTradeDate) return <p>Missing firstTradeDate</p>;
    const { dataWithLevels } = processedData;

    if (error)
        return (
            <div>
                <p>error</p>
                <pre> {JSON.stringify(error, null, 2)}</pre>
            </div>
        );

    if (loading)
        return (
            <div>
                <h3>Loading</h3>
            </div>
        );

    let topBottomInstruments: string[] = [];
    if (selectedRow.name !== "" && selectedRow.instrumentPerformances.length) {
        topBottomInstruments = getTopBottomInstrumentsFromSelectedRow(selectedRow);
    } else {
        const defaultInstrumentsValues: Record<string, number> = {};
        for (const level of dataWithLevels) {
            if (
                level.instrument &&
                level.instrument.modelType &&
                !excludeInAttribution(level.instrument.modelType as unknown as InstrumentModelTypeEnum)
            ) {
                defaultInstrumentsValues[level.instrument.name] = level.series[level.series.length - 1];
            }
        }
        topBottomInstruments = getTopBottomInstruments(defaultInstrumentsValues);
    }

    const accountDescriptions: Record<string, number> = {};
    for (const row of dataWithLevels) {
        const account = processedData.accountsById[row.accountId];
        if (account && account.type === PartyAccountType.Physical && account.description) accountDescriptions[account.description] = 1;
    }

    const rowHeight = printView === "true" ? "75mm" : "50vh";

    return (
        <>
            <div className="flex flex-wrap -mx-2 mt-3">
                <div className="w-full px-2 mb-4">
                    <h4>{selectedRow.name}</h4>
                </div>
                <div className="w-full px-2 mb-4 print:hidden">
                    <div className="flex justify-end">
                        <PrintButton />
                    </div>
                </div>
            </div>

            <div className="sticky top-0 z-20 -mx-4 px-4 py-3 mb-1 bg-gray-100/90 backdrop-blur border-b border-slate-200 print:hidden">
                <div className="flex flex-wrap items-center gap-3">
                    <PerformancePeriodSelector firstAvailableStartDate={client.firstTradeDate} endDate={endDate} />
                    <label className="inline-flex items-center gap-2 select-none text-sm text-slate-700">
                        <input
                            type="checkbox"
                            checked={printView === "true"}
                            onChange={handlePrintView}
                            className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                        />
                        Print view
                    </label>
                    <label className="inline-flex items-center gap-2 select-none text-sm text-slate-700">
                        <input
                            type="checkbox"
                            checked={sumLevelPieChart === "true"}
                            onChange={handlePieChartLevel}
                            className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                        />
                        SumAccount Attribution
                    </label>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="px-2">
                    <h4 className="text-center">Performance</h4>
                    {isValidRow(selectedRow) ? (
                        <PerformanceTimeSeriesChart timeseries={alignedTimeSeries} style={{ height: rowHeight }} showXlsxButton={false} />
                    ) : null}
                </div>
                <div className="px-2">
                    <h4 className="text-center">Key Performance Indicators (%)</h4>
                    {isValidRow(selectedRow) ? (
                        <KeyPerformanceIndicators
                            masterTimeSeriesName={selectedRow.name}
                            timeseries={alignedTimeSeries}
                            navValue={value}
                            riskFreeTimeSeries={selectedRow.riskFree}
                            locale={Language.En}
                            showItems={[
                                PerformanceKeyEnum.Value,
                                PerformanceKeyEnum.YearlyReturn,
                                PerformanceKeyEnum.Return,
                                PerformanceKeyEnum.Volatility,
                                PerformanceKeyEnum.SharpeRatio
                            ]}
                        />
                    ) : null}
                </div>
                <div className="px-2">
                    {selectedRow.performance ? (
                        <AttributionGraph
                            data={{
                                client: {
                                    ...client,
                                    accounts: client.accounts.map((account) => ({
                                        ...account,
                                        weights: [],
                                        benchmarks: account.benchmarks.map((benchmark) => ({
                                            mainBenchmark: false,
                                            showExternal: false,
                                            offset: 0,
                                            ...benchmark
                                        }))
                                    }))
                                },
                                performance: {
                                    dates: selectedRow.dateSeries,
                                    values: selectedRow.values,
                                    series: selectedRow.performance.__values,
                                    instrumentPerformances: selectedRow.instrumentPerformances as any
                                },
                                topBottomInstruments: topBottomInstruments,
                                accountDescriptions: Object.keys(accountDescriptions).sort(),
                                accountsById: processedData.accountsById
                            }}
                            selector={processedData ? processedData.attributionSelector : AttributionSelectorEnum.Instruments}
                            plotType={PlotTypeEnum.Waterfall}
                            style={{ height: rowHeight }}
                            client={client}
                            showForm={false}
                            showXlsxButton={false}
                            sumLevelAttribution={sumLevelPieChart === "true"}
                        />
                    ) : null}
                </div>
                <div className="px-2">
                    {plotData && Object.keys(plotData.positionByAccount).length > 1 ? (
                        <Fragment>
                            <h4 className="text-center">Portfolio</h4>
                            <div style={{ height: rowHeight }}>
                                <DonutChart
                                    slices={plotData.slices}
                                    colors={["#43a04c", "#2186c5", "#ba46b8", "#009688", "#9e9e9e", "#e5878a"]}
                                    hole={0}
                                    showLegend
                                    labelMode="percent"
                                    ariaLabel="Portfolio"
                                    height={360}
                                />
                            </div>
                        </Fragment>
                    ) : null}
                </div>
            </div>
        </>
    );
}
